import React, { FC, useCallback, useMemo, ChangeEvent } from 'react';
import * as _ from 'lodash';
import { joiResolver } from '@hookform/resolvers/joi';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Select, CheckboxInput } from '@td-client-frontend/common';
import { DatePicker } from '../../../../components/ReactHookForm';
import Modal from '../../../../components/Modal/NewModal';
import { defaultEmptyGroupCodes } from '../../../../utils/groups/incomingDataTransformation';
import { AddCodesBody } from './AddCodesBody';
import { EditCodesBody } from './EditCodesBody';
import { MultiEditCodes } from './MultiEditCodesBody';
import { CircleCheckSimple, CloseCircle } from '@td-client-frontend/icons';
import { codeReorganizer, codesValidation } from '../../../../utils/groups/validationSchema';
import { multiActions } from '../../../../data';
import { GroupCodes, GroupCodesWithReadOnly } from '../../../../@types/Groups';

import { useGroupContext } from '../../../../context/GroupContext';

export enum ModalType {
  Edit = 'edit',
  Add = 'add',
  MultiEdit = 'multiEdit'
}

interface GroupCodesModalProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  label: string;
  defaultValues: GroupCodes[];
  path: string;
}

const GroupCodesModal: FC<GroupCodesModalProps> = ({ onClose, onSubmit, label, defaultValues, path }) => {
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      contentType: defaultValues.length ? ModalType.Edit : ModalType.Add,
      newCodes: [defaultEmptyGroupCodes] as GroupCodes[],
      editCodes: defaultValues || [],
      checkedCodes: [] as string[],
      multiEditedCodes: [] as string[],
      multiEditAction: '',
      multiEditValue: ''
    },
    resolver: joiResolver(codesValidation)
  });

  const { setValue, getValues, watch, trigger, setError } = methods;
  const [contentType, editCodes, checkedCodes, multiEditedCodes]: [
    string,
    GroupCodesWithReadOnly[],
    string[],
    string[]
  ] = watch(['contentType', 'editCodes', 'checkedCodes', 'multiEditedCodes']);

  const resetMultiEditedState = useCallback(() => {
    multiEditedCodes.length && setValue('multiEditedCodes', []);
  }, [multiEditedCodes.length, setValue]);

  // Add new codes handlers
  const addCancel = useCallback(() => {
    setValue('newCodes', [defaultEmptyGroupCodes]);
    getValues('editCodes').length ? setValue('contentType', ModalType.Edit) : onClose();
    resetMultiEditedState();
  }, [getValues, onClose, resetMultiEditedState, setValue]);

  const addSave = useCallback(async () => {
    if (!(await trigger('newCodes'))) return;
    const addedCodes: GroupCodes[] = codeReorganizer(getValues('newCodes'));
    setValue('editCodes', [...getValues('editCodes'), ...addedCodes]);
    setValue('newCodes', [defaultEmptyGroupCodes]);
    setValue('checkedCodes', []);
    setValue('contentType', ModalType.Edit);
    resetMultiEditedState();
  }, [trigger, getValues, setValue, resetMultiEditedState]);

  // Edit codes handlers
  const editSave = useCallback(async () => {
    if (!(await trigger('editCodes'))) return;
    const codes: GroupCodes[] = codeReorganizer(getValues('editCodes'));
    onSubmit(codes);
    onClose();
    resetMultiEditedState();
  }, [trigger, getValues, onSubmit, onClose, resetMultiEditedState]);

  // Multi edit codes handlers
  const multiEditCancel = useCallback(() => {
    setValue('contentType', ModalType.Edit);
    setValue('multiEditAction', '');
    setValue('multiEditValue', '');
    setValue('checkedCodes', []);
    resetMultiEditedState();
  }, [resetMultiEditedState, setValue]);

  const multiEditSave = useCallback(() => {
    const [codesToEdit, codes, multiEditAction, multiEditValue]: [string[], GroupCodes[], string, string] = getValues([
      'checkedCodes',
      'editCodes',
      'multiEditAction',
      'multiEditValue'
    ]);
    if (!multiEditValue) return setError('multiEditValue', { message: 'Please enter a value' });

    codes
      .filter(({ code }: GroupCodes) => codesToEdit.includes(code))
      .forEach((item: GroupCodes) => (item[multiEditAction] = multiEditValue || null));
    setValue('contentType', ModalType.Edit);
    setValue('multiEditAction', '');
    setValue('multiEditValue', '');
    setValue('multiEditedCodes', codesToEdit);
  }, [getValues, setError, setValue]);

  // Modal settings
  const { component, onCancel, onSave } = useMemo(() => {
    const addNewCodesConfig = {
      component: <AddCodesBody />,
      onCancel: addCancel,
      onSave: addSave
    };

    const editCodesConfig = {
      component: <EditCodesBody path={path} />,
      onCancel: onClose,
      onSave: editSave
    };

    const multiEditCodesConfig = {
      component: <MultiEditCodes />,
      onCancel: multiEditCancel,
      onSave: multiEditSave
    };

    switch (contentType) {
      case ModalType.Add:
        return addNewCodesConfig;
      case ModalType.Edit:
        return editCodesConfig;
      case ModalType.MultiEdit:
        return multiEditCodesConfig;
      default:
        return addNewCodesConfig;
    }
  }, [addCancel, addSave, path, onClose, editSave, multiEditCancel, multiEditSave, contentType]);

  const isAllCodesChecked: boolean = useMemo(() => checkedCodes.length === editCodes.length, [checkedCodes, editCodes]);

  const handleCheckAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      if (isChecked) {
        setValue(
          'checkedCodes',
          editCodes.map(({ code }: GroupCodesWithReadOnly) => code)
        );
      } else {
        setValue('checkedCodes', []);
      }
      resetMultiEditedState();
    },
    [resetMultiEditedState, setValue, editCodes]
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      header={contentType === ModalType.Add ? `Add ${label} codes` : `Edit ${label} codes`}
      actions={[
        { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onCancel },
        {
          title: 'Save',
          kind: 'primary',
          autoClose: false,
          onClick: onSave
        }
      ]}
      size="medium"
    >
      <FormProvider {...methods}>
        <form className="p-6">
          <ModalHeaderDescription />
          {contentType !== ModalType.Add && editCodes.length > 1 && (
            <MultiEditHeader onSave={multiEditSave} onCancel={multiEditCancel} path={path} />
          )}
          <div className="grid grid-cols-12 items-center gap-4 border-b-1 border-gray px-2">
            {contentType === ModalType.Edit && editCodes.length > 1 && (
              <CheckboxInput
                checked={isAllCodesChecked}
                onChange={handleCheckAll}
                wrapperClasses="col-span-1"
                className="p-1"
              />
            )}
            <h3 className="py-4 text-left text-gray-default text-sm font-medium col-span-4">CODE</h3>
            <h3 className="py-4 text-left text-gray-default text-sm font-medium col-span-3">EFFECTIVE DATE</h3>
            <h3 className="py-4 text-left text-gray-default text-sm font-medium col-span-3">TERMINATION DATE</h3>
          </div>
          {component}
        </form>
      </FormProvider>
    </Modal>
  );
};

export default GroupCodesModal;

const ModalHeaderDescription = () => (
  <div className="w-full">
    <h2 className="pb-4 font-effra text-base font-medium">
      Enter in a code and select an effective date. To bulk add please enter into the box and separate using a
      semi-colon - Example: A01;A03;A05;
    </h2>
  </div>
);

const MultiEditHeader: FC<{ onSave: () => void; onCancel: () => void; path: string }> = ({
  onSave,
  onCancel,
  path
}) => {
  const { initialValues } = useGroupContext();
  const initCodes: GroupCodes[] = useMemo(() => _.get(initialValues, path, []) || [], [initialValues, path]);
  const { watch, setValue } = useFormContext();
  const [contentType, editCodes, checkedCodes, multiEditAction]: [string, GroupCodes[], string[], string] = watch([
    'contentType',
    'editCodes',
    'checkedCodes',
    'multiEditAction'
  ]);

  const handleCloseDeleteModal = useCallback(() => setValue('multiEditAction', ''), [setValue]);
  const handleSubmitDeleteModal = useCallback(() => {
    setValue(
      'editCodes',
      editCodes.filter(
        ({ code }: GroupCodes) => !checkedCodes.includes(code) || initCodes.some((i: GroupCodes) => i.code === code)
      )
    );
    setValue('multiEditAction', '');
  }, [setValue, editCodes, checkedCodes, initCodes]);

  const handleActionSelect = useCallback(
    (value) => {
      setValue('multiEditAction', value);
      if (value !== 'delete') {
        setValue('contentType', ModalType.MultiEdit);
      }
    },
    [setValue]
  );

  return (
    <>
      <div className="flex justify-between w-full">
        <div className="w-72">
          <Select
            options={multiActions}
            value={multiEditAction}
            onChange={handleActionSelect}
            placeholder="Actions"
            maxVisible={3}
            wrapperClasses="col-span-5"
            isDisabled={!checkedCodes.length}
          />
        </div>
        {contentType === ModalType.MultiEdit && (multiEditAction === 'end_dt' || multiEditAction === 'start_dt') && (
          <div className="flex items-start space-x-4">
            <DatePicker name="multiEditValue" placeholder="MM/DD/YYYY" />
            <button onClick={onSave} className="mt-4" type="button">
              <CircleCheckSimple color="#167F01" />
            </button>
            <button onClick={onCancel} type="button" className="mt-4">
              <CloseCircle className="stroke-current text-red-700 cursor-pointer" />
            </button>
          </div>
        )}
      </div>
      {/* Modal */}
      {multiEditAction === 'delete' && (
        <DeletePlanModal onClose={handleCloseDeleteModal} onSubmit={handleSubmitDeleteModal} />
      )}
    </>
  );
};

const DeletePlanModal: FC<{ onClose: () => void; onSubmit: () => void }> = ({ onClose, onSubmit }) => (
  <Modal
    isOpen
    header=""
    onClose={onClose}
    actions={[
      { title: 'Cancel', kind: 'secondary', autoClose: false, onClick: onClose },
      {
        title: 'Delete',
        kind: 'primary',
        autoClose: false,
        onClick: onSubmit
      }
    ]}
    size="prompt"
  >
    <h6 className="px-3 font-effra text-2xl text-black-default font-medium">
      Are you sure you wish to delete these codes ?
    </h6>
  </Modal>
);
